import { useMemo, useState } from 'react';
import { Linking, Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import { ArrowDown, ArrowUp, ArrowUpDown, Clock, Edit3, Bell, ShoppingCart, PackagePlus, Stethoscope, Trash2 } from 'lucide-react-native';
import type { LucideIcon } from 'lucide-react-native';

import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { colors, radius, typography } from '@/theme/tokens';
import { diasHasta, formatDate, whatsappLink } from '@/utils/format';
import type { ProductoVenta, InsumoInterno } from '@/types/domain';

type Producto = ProductoVenta | InsumoInterno;
type Tipo = 'venta' | 'interno';
type Rol = 'admin' | 'doctor' | 'recepcion';
type SortKey = 'nombre' | 'categoria' | 'stock' | 'vencimiento' | 'proveedor';
type SortDir = 'asc' | 'desc';

interface Props {
  productos: Producto[];
  tipo: Tipo;
  rol: Rol;
  onEdit?: (p: Producto) => void;
  onEliminar?: (p: Producto) => void;
  onVender?: (p: Producto) => void;
  onUsar?: (p: Producto) => void;
  onIngresarStock?: (p: Producto) => void;
  onAvisarReposicion?: (p: Producto) => void;
  onVerHistorial?: (p: Producto) => void;
}

export function ProductosTable({
  productos,
  tipo,
  rol,
  onEdit,
  onEliminar,
  onVender,
  onUsar,
  onIngresarStock,
  onAvisarReposicion,
  onVerHistorial,
}: Props) {
  const { width } = useWindowDimensions();
  const wideEnough = width >= 1100;
  const [sortKey, setSortKey] = useState<SortKey>('nombre');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  // Doctora en Farmacia: solo lee. Sin columna Acciones.
  const hideAcciones = rol === 'doctor' && tipo === 'venta';

  const sorted = useMemo(() => {
    const arr = [...productos];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'nombre':
          cmp = a.nombre.localeCompare(b.nombre);
          break;
        case 'categoria':
          cmp = (a.categoria ?? '').localeCompare(b.categoria ?? '');
          break;
        case 'stock':
          cmp = a.stock - b.stock;
          break;
        case 'vencimiento': {
          const av = a.vencimiento?.toMillis?.() ?? Number.POSITIVE_INFINITY;
          const bv = b.vencimiento?.toMillis?.() ?? Number.POSITIVE_INFINITY;
          cmp = av - bv;
          break;
        }
        case 'proveedor':
          cmp = (a.proveedorNombre ?? '').localeCompare(b.proveedorNombre ?? '');
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [productos, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(k);
      setSortDir('asc');
    }
  };

  // Si la ventana es lo suficientemente ancha, no hace falta scroll horizontal:
  // la tabla se expande al 100 % del padre. En móvil/zoom alto, scrolleable.
  const Wrapper = wideEnough
    ? ({ children }: { children: React.ReactNode }) => <View style={{ width: '100%' }}>{children}</View>
    : ({ children }: { children: React.ReactNode }) => (
        <ScrollView horizontal showsHorizontalScrollIndicator>
          <View style={{ minWidth: 880 }}>{children}</View>
        </ScrollView>
      );

  return (
    <View style={{ borderRadius: radius['2xl'], backgroundColor: colors.white, overflow: 'hidden', borderWidth: 1, borderColor: colors.ink[100] }}>
      <Wrapper>
        <View style={{ width: '100%' }}>
          {/* Header tabla */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'stretch',
              paddingVertical: 12,
              backgroundColor: colors.crema[50],
              borderBottomWidth: 1,
              borderBottomColor: colors.ink[100],
            }}
          >
            <HeaderCell flex={1.6} minWidth={180}>
              <SortHeader label="Producto" k="nombre" sortKey={sortKey} sortDir={sortDir} onPress={toggleSort} />
            </HeaderCell>
            <HeaderCell width={150} align="center">
              <SortHeader label="Categoría" k="categoria" sortKey={sortKey} sortDir={sortDir} onPress={toggleSort} align="center" />
            </HeaderCell>
            <HeaderCell width={120} align="center">
              <SortHeader label="Stock" k="stock" sortKey={sortKey} sortDir={sortDir} onPress={toggleSort} align="center" />
            </HeaderCell>
            <HeaderCell width={110} align="center">
              <SortHeader label="Vencimiento" k="vencimiento" sortKey={sortKey} sortDir={sortDir} onPress={toggleSort} align="center" />
            </HeaderCell>
            <HeaderCell width={140} align="center" last={hideAcciones}>
              <SortHeader label="Proveedor" k="proveedor" sortKey={sortKey} sortDir={sortDir} onPress={toggleSort} align="center" />
            </HeaderCell>
            {!hideAcciones ? (
              <HeaderCell width={290} align="right" last>
                <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 1.4, textTransform: 'uppercase', textAlign: 'right' }}>
                  Acciones
                </Text>
              </HeaderCell>
            ) : null}
          </View>

          {/* Filas */}
          {sorted.map((p, idx) => (
            <FilaProducto
              key={p.id}
              p={p}
              tipo={tipo}
              rol={rol}
              hideAcciones={hideAcciones}
              alt={idx % 2 === 1}
              onEdit={onEdit}
              onEliminar={onEliminar}
              onVender={onVender}
              onUsar={onUsar}
              onIngresarStock={onIngresarStock}
              onAvisarReposicion={onAvisarReposicion}
              onVerHistorial={onVerHistorial}
              isLast={idx === sorted.length - 1}
            />
          ))}
        </View>
      </Wrapper>
    </View>
  );
}

function HeaderCell({
  children,
  flex,
  width,
  minWidth,
  align = 'left',
  last,
}: {
  children: React.ReactNode;
  flex?: number;
  width?: number;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  last?: boolean;
}) {
  return (
    <View
      style={{
        flex,
        width,
        minWidth,
        paddingHorizontal: 14,
        justifyContent: 'center',
        alignItems: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
        borderRightWidth: last ? 0 : 1,
        borderRightColor: colors.ink[100],
      }}
    >
      {children}
    </View>
  );
}

function Cell({
  children,
  flex,
  width,
  minWidth,
  align = 'left',
  last,
}: {
  children: React.ReactNode;
  flex?: number;
  width?: number;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  last?: boolean;
}) {
  return (
    <View
      style={{
        flex,
        width,
        minWidth,
        paddingHorizontal: 14,
        justifyContent: 'center',
        alignItems: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'stretch',
        borderRightWidth: last ? 0 : 1,
        borderRightColor: colors.ink[100],
      }}
    >
      {children}
    </View>
  );
}

function SortHeader({
  label,
  k,
  sortKey,
  sortDir,
  onPress,
  align = 'left',
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onPress: (k: SortKey) => void;
  align?: 'left' | 'right' | 'center';
}) {
  const active = sortKey === k;
  const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <Pressable
      onPress={() => onPress(k)}
      style={({ hovered }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        opacity: hovered ? 0.7 : 1,
        // @ts-ignore web only
        cursor: 'pointer',
        justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
      })}
    >
      <Text
        style={{
          fontSize: 11,
          fontWeight: typography.weights.bold,
          color: active ? colors.magenta[700] : colors.ink[500],
          letterSpacing: 1.4,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Text>
      <Icon size={12} color={active ? colors.magenta[700] : colors.ink[400]} />
    </Pressable>
  );
}

function FilaProducto({
  p,
  tipo,
  rol,
  hideAcciones,
  alt,
  isLast,
  onEdit,
  onEliminar,
  onVender,
  onUsar,
  onIngresarStock,
  onAvisarReposicion,
  onVerHistorial,
}: {
  p: Producto;
  tipo: Tipo;
  rol: Rol;
  hideAcciones: boolean;
  alt: boolean;
  isLast: boolean;
  onEdit?: (p: Producto) => void;
  onEliminar?: (p: Producto) => void;
  onVender?: (p: Producto) => void;
  onUsar?: (p: Producto) => void;
  onIngresarStock?: (p: Producto) => void;
  onAvisarReposicion?: (p: Producto) => void;
  onVerHistorial?: (p: Producto) => void;
}) {
  const venc = p.vencimiento?.toDate?.() ?? null;
  const dias = diasHasta(venc);

  const agotado = p.stock === 0;
  const bajo = !agotado && p.stockBajo;
  const stockColor = agotado ? colors.danger[600] : bajo ? colors.warning[600] : colors.success[600];
  const stockBg = agotado ? colors.danger[50] : bajo ? colors.warning[50] : colors.success[50];
  const stockEstado = agotado ? 'Agotado' : bajo ? 'Bajo' : 'Disponible';

  const onWhatsapp = () => {
    if (!p.proveedorWhatsapp) return;
    const msg = `Hola${p.proveedorNombre ? ` ${p.proveedorNombre}` : ''}, te escribo de CliniCAT. Necesito reponer ${p.nombre} (${p.presentacion}). ¿Tenés disponibilidad?`;
    Linking.openURL(whatsappLink(p.proveedorWhatsapp, msg));
  };

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'stretch',
        paddingVertical: 12,
        backgroundColor: alt ? 'rgba(250,247,244,0.5)' : colors.white,
        borderBottomWidth: isLast ? 0 : 1,
        borderBottomColor: colors.ink[100],
      }}
    >
      {/* Producto: nombre + presentación */}
      <Cell flex={1.6} minWidth={180}>
        <Text style={{ fontSize: 14, fontWeight: typography.weights.bold, color: colors.ink[900] }} numberOfLines={1}>
          {p.nombre}
        </Text>
        <Text style={{ fontSize: 12, color: colors.ink[500], marginTop: 1 }} numberOfLines={1}>
          {p.presentacion}
        </Text>
      </Cell>

      {/* Categoría */}
      <Cell width={150} align="center">
        <Text style={{ fontSize: 13, color: colors.ink[700], textAlign: 'center' }} numberOfLines={2}>
          {p.categoria ?? '—'}
        </Text>
      </Cell>

      {/* Stock — número grande + estado en chip debajo */}
      <Cell width={120} align="center">
        <View style={{ alignItems: 'center', gap: 2 }}>
          <Text
            style={{
              fontFamily: typography.display.fontFamily,
              fontSize: 22,
              fontWeight: typography.weights.bold,
              color: stockColor,
              lineHeight: 24,
            }}
          >
            {p.stock}
          </Text>
          <View
            style={{
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 999,
              backgroundColor: stockBg,
            }}
          >
            <Text
              style={{
                fontSize: 10,
                fontWeight: typography.weights.bold,
                color: stockColor,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
              }}
            >
              {stockEstado}
            </Text>
          </View>
        </View>
      </Cell>

      {/* Vencimiento */}
      <Cell width={110} align="center">
        {venc ? (
          <View style={{ alignItems: 'center' }}>
            <Text style={{ fontSize: 13, color: colors.ink[800], textAlign: 'center' }}>{formatDate(venc)}</Text>
            {dias !== null ? (
              <Text
                style={{
                  fontSize: 11,
                  color: dias < 0 ? colors.danger[600] : dias <= 30 ? colors.danger[600] : dias <= 60 ? colors.warning[600] : colors.ink[500],
                  marginTop: 1,
                  textAlign: 'center',
                }}
              >
                {dias < 0 ? 'Vencido' : `${dias}d`}
              </Text>
            ) : null}
          </View>
        ) : (
          <Text style={{ color: colors.ink[400], fontSize: 13, textAlign: 'center' }}>—</Text>
        )}
      </Cell>

      {/* Proveedor */}
      <Cell width={140} align="center" last={hideAcciones}>
        <Text style={{ fontSize: 13, color: colors.ink[600], textAlign: 'center' }} numberOfLines={2}>
          {p.proveedorNombre ?? '—'}
        </Text>
      </Cell>

      {hideAcciones ? null : (
      <Cell width={290} align="right" last>
        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 8, paddingLeft: 24 }}>
          {/* Slot 1: Salida / Usar — 78px. Plomo cuando stock=0 */}
          <ActionSlot width={78}>
            {tipo === 'venta' && (rol === 'admin' || rol === 'recepcion') ? (
              <ActionBtn
                label="Salida"
                Icon={ShoppingCart}
                variant={p.stock > 0 ? 'success' : 'disabled'}
                onPress={p.stock > 0 ? () => onVender?.(p) : undefined}
              />
            ) : tipo === 'interno' && (rol === 'admin' || rol === 'doctor') ? (
              <ActionBtn
                label="Usar"
                Icon={Stethoscope}
                variant={p.stock > 0 ? 'primary' : 'disabled'}
                onPress={p.stock > 0 ? () => onUsar?.(p) : undefined}
              />
            ) : null}
          </ActionSlot>

          {/* Slot 2: Avisar (no-admin) o Reponer (admin) — 88px */}
          <ActionSlot width={88}>
            {rol === 'admin' && onIngresarStock ? (
              <ActionBtn label="Reponer" Icon={PackagePlus} variant="outline" onPress={() => onIngresarStock(p)} />
            ) : (p.stockBajo || p.stock === 0) && onAvisarReposicion ? (
              <ActionBtn label="Avisar" Icon={Bell} variant="warning" onPress={() => onAvisarReposicion(p)} />
            ) : null}
          </ActionSlot>

          {/* Slot 3: WhatsApp — 30px (solo admin contacta proveedores) */}
          <ActionSlot width={30}>
            {rol === 'admin' && p.stockBajo && p.proveedorWhatsapp ? (
              <Pressable
                onPress={onWhatsapp}
                accessibilityLabel="WhatsApp"
                style={({ hovered }) => ({
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: hovered ? '#10AC84' : '#25D366',
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
              >
                <WhatsAppIcon size={16} color={colors.white} />
              </Pressable>
            ) : null}
          </ActionSlot>

          {/* Slot historial — 30px (lo ve cualquiera) */}
          <ActionSlot width={30}>
            {onVerHistorial ? (
              <Pressable
                onPress={() => onVerHistorial(p)}
                style={({ hovered }) => ({
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: hovered ? colors.magenta[50] : 'transparent',
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
                accessibilityLabel="Ver historial de stock"
              >
                <Clock size={14} color={colors.magenta[700]} />
              </Pressable>
            ) : null}
          </ActionSlot>

          {/* Slot 4: Editar — 30px */}
          <ActionSlot width={30}>
            {rol === 'admin' && onEdit ? (
              <Pressable
                onPress={() => onEdit(p)}
                style={({ hovered }) => ({
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: hovered ? colors.ink[100] : 'transparent',
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
                accessibilityLabel="Editar"
              >
                <Edit3 size={14} color={colors.ink[600]} />
              </Pressable>
            ) : null}
          </ActionSlot>

          {/* Slot 5: Eliminar — 30px */}
          <ActionSlot width={30}>
            {rol === 'admin' && onEliminar ? (
              <Pressable
                onPress={() => {
                  const ok =
                    typeof globalThis !== 'undefined' && typeof (globalThis as any).confirm === 'function'
                      ? (globalThis as any).confirm(`¿Eliminar "${p.nombre} (${p.presentacion})"? Esta acción no se puede deshacer.`)
                      : true;
                  if (ok) onEliminar(p);
                }}
                style={({ hovered }) => ({
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: hovered ? 'rgba(239,68,68,0.12)' : 'transparent',
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
                accessibilityLabel="Eliminar"
              >
                <Trash2 size={14} color={colors.danger[600]} />
              </Pressable>
            ) : null}
          </ActionSlot>
        </View>
      </Cell>
      )}
    </View>
  );
}

function ActionSlot({ width, children }: { width: number; children: React.ReactNode }) {
  return (
    <View style={{ width, alignItems: 'center', justifyContent: 'center' }}>{children}</View>
  );
}

function ActionBtn({
  label,
  Icon,
  onPress,
  variant,
  ariaLabel,
  fullWidth,
}: {
  label: string;
  Icon: LucideIcon;
  onPress?: () => void;
  variant: 'success' | 'primary' | 'warning' | 'outline' | 'whatsapp' | 'disabled';
  ariaLabel?: string;
  fullWidth?: boolean;
}) {
  const palette = {
    success: { bg: colors.success[500], hover: colors.success[600], fg: colors.white, border: 'transparent' },
    primary: { bg: colors.magenta[700], hover: colors.magenta[600], fg: colors.white, border: 'transparent' },
    warning: { bg: colors.warning[500], hover: colors.warning[600], fg: colors.white, border: 'transparent' },
    outline: { bg: colors.white, hover: colors.magenta[50], fg: colors.magenta[700], border: colors.magenta[200] },
    whatsapp: { bg: '#0F9D58', hover: '#10AC84', fg: colors.white, border: 'transparent' },
    disabled: { bg: colors.ink[200], hover: colors.ink[200], fg: colors.ink[500], border: 'transparent' },
  }[variant];
  const isDisabled = variant === 'disabled';

  const iconOnly = !label;

  return (
    <Pressable
      onPress={isDisabled ? undefined : onPress}
      disabled={isDisabled}
      accessibilityLabel={ariaLabel ?? label}
      style={({ hovered }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: iconOnly ? 0 : 5,
        paddingHorizontal: iconOnly ? 0 : 10,
        width: iconOnly ? 30 : fullWidth ? '100%' : undefined,
        height: 30,
        borderRadius: 9,
        backgroundColor: hovered && !isDisabled ? palette.hover : palette.bg,
        borderWidth: variant === 'outline' ? 1 : 0,
        borderColor: palette.border,
        opacity: isDisabled ? 0.7 : 1,
        // @ts-ignore web only
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        // @ts-ignore web only
        transitionProperty: 'background-color',
        // @ts-ignore web only
        transitionDuration: '160ms',
      })}
    >
      <Icon size={iconOnly ? 14 : 12} color={palette.fg} />
      {iconOnly ? null : (
        <Text style={{ color: palette.fg, fontSize: 11, fontWeight: typography.weights.bold }}>{label}</Text>
      )}
    </Pressable>
  );
}
